import type { DashboardInsight, InsightSeverity } from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { formatMoney } from './money';

/**
 * صياغة اللوحة: الخادم يرسل أرقامًا وقواعد، وهنا تصير جملًا عربية. خالص بلا React حتى
 * يُختبر وحده — والحساب بـ `Money`، والتحويل إلى نص للعرض فقط.
 */

export const SEVERITY_LABEL: Record<InsightSeverity, string> = {
  critical: 'عاجل',
  warning: 'انتبه',
  info: 'معلومة',
  good: 'خبر جيد',
};

const WEEKDAYS = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

const MONTHS_AR = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
];

export function monthLabel(month: string): string {
  return MONTHS_AR[Number(month.slice(5, 7)) - 1] ?? month;
}

/** «الثلاثاء ٢١ أكتوبر» — اليوم من التقويم لا من المنطقة الزمنية. */
export function dayLabel(date: string): string {
  const day = new Date(
    Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10))),
  ).getUTCDay();
  return WEEKDAYS[day] + ' ' + Number(date.slice(8, 10)) + ' ' + monthLabel(date.slice(0, 7));
}

export function shortDate(date: string): string {
  return Number(date.slice(8, 10)) + ' ' + monthLabel(date.slice(0, 7)) + ' ' + date.slice(0, 4);
}

export function greeting(hour: number): string {
  if (hour < 5) return 'مساء الخير';
  if (hour < 12) return 'صباح الخير';
  return 'مساء الخير';
}

/** مبلغ مختصر لمحاور الرسوم والبطاقات الضيقة: ١٢٫٥ ألف، ١٫٢ مليون. */
export function formatCompact(amount: string | Money): string {
  const value = Money.of(amount);
  const abs = value.abs();
  const sign = value.isNegative() ? '-' : '';
  if (abs.gte(1_000_000)) return sign + trim(abs.div(1_000_000).toFixed(1)) + ' مليون';
  if (abs.gte(1_000)) return sign + trim(abs.div(1_000).toFixed(1)) + ' ألف';
  return sign + trim(abs.toFixed(0));
}

function trim(fixed: string): string {
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
}

export interface Delta {
  /** نسبة التغير بلا إشارة، أو null حين لا أساس للمقارنة. */
  percent: string | null;
  direction: 'up' | 'down' | 'flat';
}

/** تغير القيمة عن المقارنة — النسبة على القيمة المطلقة للسابق حتى لا تنقلب إشارة الخسارة. */
export function delta(value: string, previous: string): Delta {
  const now = Money.of(value);
  const before = Money.of(previous);
  const diff = now.sub(before);
  const direction = diff.isZero() ? 'flat' : diff.isPositive() ? 'up' : 'down';
  if (before.isZero()) return { percent: null, direction };
  return { percent: diff.abs().mul(100).div(before.abs()).toFixed(0), direction };
}

/** فرق نقاط الهامش ٪. */
export function pointsDelta(value: string | null, previous: string | null): Delta {
  if (value === null || previous === null) return { percent: null, direction: 'flat' };
  const diff = Money.of(value).sub(previous);
  return {
    percent: diff.abs().toFixed(1),
    direction: diff.isZero() ? 'flat' : diff.isPositive() ? 'up' : 'down',
  };
}

function money(amount: string | null, currency: string): string {
  return formatMoney(amount ?? '0') + ' ' + currency;
}

function percent(value: string): string {
  return trim(Money.of(value).abs().toFixed(1)) + '٪';
}

export interface InsightText {
  title: string;
  detail: string | null;
}

/** جملة التنبيه من قاعدته وأرقامه. */
export function insightText(insight: DashboardInsight, currency: string): InsightText {
  const { amount, previous, subject, count, date, value } = insight;
  switch (insight.rule) {
    case 'revenue_change':
      return {
        title: Money.of(value).isNegative()
          ? 'الإيراد نزل ' + percent(value) + ' عن فترة المقارنة'
          : 'الإيراد زاد ' + percent(value) + ' عن فترة المقارنة',
        detail: money(amount, currency) + ' مقابل ' + money(previous, currency),
      };
    case 'expense_spike':
      return {
        title: 'المصروفات زادت ' + percent(value) + ' عن فترة المقارنة',
        detail: subject
          ? 'أكبر بند: ' + subject.name + ' (+' + money(amount, currency) + ')'
          : null,
      };
    case 'margin_change':
      return {
        title:
          'هامش مجمل الربح ' +
          (previous !== null && Money.of(value).lt(previous) ? 'نزل' : 'ارتفع') +
          ' من ' +
          percent(previous ?? '0') +
          ' إلى ' +
          percent(value),
        detail: null,
      };
    case 'net_loss':
      return {
        title: 'الفترة خاسرة: صافى خسارة ' + money(Money.of(value).abs().toString(), currency),
        detail: previous !== null ? 'فترة المقارنة: ' + money(previous, currency) : null,
      };
    case 'overdue_customer':
      return {
        title:
          'العميل ' +
          (subject?.name ?? '') +
          ' متأخر أكثر من ' +
          value +
          ' يومًا بمبلغ ' +
          money(amount, currency),
        detail: count !== null && count > 3 ? 'ومعه ' + (count - 3) + ' عملاء آخرون متأخرون' : null,
      };
    case 'cash_runway':
      if (value === 'growing') {
        return {
          title: 'النقدية فى زيادة — التحصيل يغطى الصرف',
          detail: 'متوسط صافى الزيادة ' + money(amount, currency) + ' شهريًا',
        };
      }
      return {
        title: 'النقدية تكفى حوالى ' + trim(value) + ' شهر بمعدل الصرف الحالى',
        detail: 'متوسط الصرف الشهرى ' + money(amount, currency),
      };
    case 'slow_moving_stock':
      return {
        title: value + ' أصناف راكدة قيمتها ' + money(amount, currency),
        detail: subject ? 'أكبرها قيمة: ' + subject.name : null,
      };
    case 'vat_due':
      return {
        title: 'ضريبة القيمة المضافة المستحقة هذا الشهر حوالى ' + money(amount, currency),
        detail: date ? 'موعد الإقرار ' + shortDate(date) : null,
      };
    case 'large_unpaid_invoices':
      return {
        title:
          (count ?? value) + ' فواتير غير محصّلة كل منها ' + money(previous, currency) + ' أو أكثر',
        detail:
          'إجماليها ' + money(amount, currency) + (subject ? ' — أكبرها على ' + subject.name : ''),
      };
    case 'supplier_due_soon':
      return {
        title:
          (count ?? 0) +
          ' فواتير موردين تستحق خلال ' +
          value +
          ' أيام بإجمالى ' +
          money(amount, currency),
        detail:
          (date ? 'أقربها ' + shortDate(date) : '') +
          (subject ? ' — أكبر مورد: ' + subject.name : ''),
      };
    case 'best_sales_day':
      return {
        title:
          'أفضل يوم مبيعات هذا الأسبوع: ' +
          (date ? dayLabel(date) : '') +
          ' بإيراد ' +
          money(value, currency),
        detail: 'إجمالى آخر ٧ أيام ' + money(amount, currency),
      };
  }
}
